/**
 * Staff Console → Lifecycle tab.
 *
 * Onboarding funnel view for the lifecycle email automation (campaigns
 * worker cron at :07/:37 — see orchestrator/lifecycle-emails.ts). Top row
 * counts users per stage; below it, each tracked user with their current
 * stage and every reminder they've received. Emails only go to users who
 * signed up after the feature launched, so early rows may be sparse.
 */

import { Loader2, RefreshCw, CreditCard, Link2, Users, Rocket, CheckCircle2, Megaphone } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { fmtLocale } from '@/lib/dates'
import { useLifecycle, type LifecycleEmail } from './use-admin'

function money(n: number | null): string {
  if (n === null || !Number.isFinite(n)) return '—'
  return '$' + Math.round(n).toLocaleString()
}

const STAGES: { key: string; label: string; icon: LucideIcon; tone: string }[] = [
  { key: 'awaiting_payment', label: 'Awaiting payment', icon: CreditCard, tone: 'text-[#F87171]' },
  { key: 'connect_linkedin', label: 'Connect LinkedIn', icon: Link2, tone: 'text-[#FBBF24]' },
  { key: 'add_leads', label: 'Add leads', icon: Users, tone: 'text-[#FBBF24]' },
  { key: 'launch_campaign', label: 'Launch campaign', icon: Rocket, tone: 'text-[#8FB4FA]' },
  { key: 'complete', label: 'Complete', icon: CheckCircle2, tone: 'text-[#34D399]' },
]

function stageBadgeClass(stage: string): string {
  if (stage === 'complete') return 'bg-[rgba(16,185,129,0.12)] text-[#34D399]'
  if (stage === 'awaiting_payment') return 'bg-[rgba(248,113,113,0.12)] text-[#F87171]'
  if (stage === 'launch_campaign') return 'bg-[rgba(59,130,246,0.12)] text-[#8FB4FA]'
  return 'bg-[rgba(251,191,36,0.12)] text-[#FBBF24]'
}

function remindersFor(emails: LifecycleEmail[], userId: string): LifecycleEmail[] {
  return emails.filter((e) => e.user_id === userId)
}

/** utm_source wins, then referrer host, else 'direct' — mirrors the API rollup. */
function sourceLabel(s: { utm_source: string | null; referrer: string | null }): string {
  if (s.utm_source) return s.utm_source
  if (s.referrer) {
    try {
      return new URL(s.referrer).host.replace(/^www\./, '')
    } catch {
      return s.referrer.slice(0, 30)
    }
  }
  return 'direct'
}

export function LifecyclePage() {
  const { data, isLoading, refetch, isRefetching } = useLifecycle()

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center p-12 text-[var(--color-silver)]">
        <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading lifecycle data…
      </div>
    )
  }

  const countFor = (stage: string) =>
    data.stage_counts.find((s) => s.stage === stage)?.n ?? 0

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        {STAGES.map((s) => (
          <Card key={s.key} className="flex-1 min-w-[150px]">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <p className="eyebrow text-[var(--color-silver)]">{s.label}</p>
                <s.icon className={cn('h-4 w-4', s.tone)} />
              </div>
              <div className={cn('mt-1 text-2xl font-bold tracking-tight', s.tone)}>
                {countFor(s.key).toLocaleString()}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {data.source_counts.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base text-[var(--color-silver-bright)]">
              <Megaphone className="h-4 w-4 text-[#8FB4FA]" />
              Signups by source
              <span className="text-xs font-normal text-[var(--color-silver)]">
                (all-time · utm_source, else referrer domain, else direct)
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {data.source_counts.map((s) => (
                <Badge
                  key={s.source}
                  variant="outline"
                  className="border-none bg-[rgba(255,255,255,0.06)] px-3 py-1.5 text-sm text-[var(--color-silver-bright)]"
                >
                  {s.source}
                  <span className="ml-2 font-bold text-[#8FB4FA] tabular-nums">{s.n}</span>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base text-[var(--color-silver-bright)]">
            <Megaphone className="h-4 w-4 text-[#34D399]" />
            Calculator leads
            <span className="text-xs font-normal text-[var(--color-silver)]">
              (emails captured on the marketing site · newest first)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.captured_leads.length === 0 ? (
            <p className="py-4 text-sm text-[var(--color-silver)]">
              No leads captured yet. The earnings calculator posts here the moment
              someone submits their email.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead className="text-right">Deal size</TableHead>
                  <TableHead className="text-right">Calls/mo</TableHead>
                  <TableHead className="text-right">Projected</TableHead>
                  <TableHead>Numbers sent</TableHead>
                  <TableHead>Captured</TableHead>
                  <TableHead>Signed up</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.captured_leads.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell className="text-sm text-[var(--color-silver-bright)]">{l.email}</TableCell>
                    <TableCell className="text-xs">{l.utm_source ?? l.source}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{money(l.avg_deal_value)}</TableCell>
                    <TableCell className="text-right text-xs tabular-nums">{l.calls_per_month ?? '—'}</TableCell>
                    <TableCell className="text-right text-xs font-semibold tabular-nums text-[#34D399]">
                      {money(l.projected_revenue)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {l.emailed_at ? (
                        <span className="text-[#34D399]">sent</span>
                      ) : (
                        <span className="text-[var(--color-silver)]">no</span>
                      )}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs tabular-nums">{fmtLocale(l.created_at)}</TableCell>
                    <TableCell>
                      {l.converted > 0 ? (
                        <Badge variant="outline" className="border-none bg-[rgba(16,185,129,0.12)] text-xs text-[#34D399]">
                          converted
                        </Badge>
                      ) : (
                        <span className="text-xs text-[var(--color-silver)]">not yet</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-base text-[var(--color-silver-bright)]">
            Onboarding funnel
            <span className="ml-2 text-xs font-normal text-[var(--color-silver)]">
              (stage per user + reminders sent · checks run at :07 and :37)
            </span>
          </CardTitle>
          <button
            onClick={() => void refetch()}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs text-[var(--color-silver)] hover:bg-[rgba(255,255,255,0.05)]"
          >
            <RefreshCw className={cn('h-3.5 w-3.5', isRefetching && 'animate-spin')} />
            Refresh
          </button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>In stage since</TableHead>
                <TableHead>Signed up</TableHead>
                <TableHead>Reminders sent</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.states.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="py-6 text-center text-sm text-[var(--color-silver)]">
                    No users tracked yet. Rows appear on the first cron pass after a
                    new signup (only users created after the feature launch are tracked).
                  </TableCell>
                </TableRow>
              )}
              {data.states.map((s) => {
                const sent = remindersFor(data.emails, s.user_id)
                return (
                  <TableRow key={s.user_id}>
                    <TableCell>
                      <div className="text-sm font-medium text-[var(--color-silver-bright)]">
                        {s.full_name ?? '(no name)'}
                      </div>
                      <div className="text-xs text-[var(--color-silver)]">{s.email}</div>
                    </TableCell>
                    <TableCell className="text-xs" title={s.utm_campaign ?? s.referrer ?? ''}>
                      {sourceLabel(s)}
                    </TableCell>
                    <TableCell className="text-xs">
                      {s.plan}
                      {s.plan_status !== 'active' && (
                        <span className="ml-1 text-[#FBBF24]">({s.plan_status})</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cn('border-none text-xs', stageBadgeClass(s.stage))}>
                        {s.stage.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs tabular-nums">
                      {fmtLocale(s.entered_at)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs tabular-nums">
                      {fmtLocale(s.signed_up_at)}
                    </TableCell>
                    <TableCell>
                      {sent.length === 0 ? (
                        <span className="text-xs text-[var(--color-silver)]">none</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {sent.map((e) => (
                            <Badge
                              key={e.email_key}
                              variant="outline"
                              className="border-none bg-[rgba(255,255,255,0.06)] text-xs text-[var(--color-silver)]"
                              title={fmtLocale(e.sent_at)}
                            >
                              {e.email_key}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}
